<script setup lang="ts">
  import { ref, onMounted, computed, watch } from 'vue'
  import { X, ChevronLeft, ChevronRight } from 'lucide-vue-next'
  import { MegaphoneIcon } from '@heroicons/vue/24/outline'
  import DOMPurify from 'dompurify'
  import { AnnouncementsService } from '@/types/generated/services.gen'
  import type { Announcement } from '@/types/generated/types.gen'
  import { DismissalStateEnum } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      hideForNow: 'Hide for now',
      dismiss: 'Dismiss',
      previous: 'Previous announcement',
      next: 'Next announcement',
      learnMore: 'Learn more'
    },
    de: {
      hideForNow: 'Vorerst ausblenden',
      dismiss: 'Schließen',
      previous: 'Vorherige Ankündigung',
      next: 'Nächste Ankündigung',
      learnMore: 'Mehr erfahren'
    }
  })

  const announcements = ref<Announcement[]>([])
  const currentIndex = ref(0)

  const visibleAnnouncements = computed(() =>
    (announcements.value ?? []).filter((a) => a.dismissal_state !== DismissalStateEnum.HIDDEN)
  )

  const currentAnnouncement = computed(() => visibleAnnouncements.value[currentIndex.value])

  const hasMultipleAnnouncements = computed(() => visibleAnnouncements.value.length > 1)

  const canGoPrev = computed(() => currentIndex.value > 0)

  const canGoNext = computed(() => currentIndex.value < visibleAnnouncements.value.length - 1)

  const safeLinkUrl = computed(() => {
    const url = currentAnnouncement.value?.link_url
    if (!url) return null
    try {
      const parsed = new URL(url, window.location.origin)
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href
      }
      return null
    } catch {
      return null
    }
  })

  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false
    })
  }

  const styleConfig = {
    INFO: {
      container:
        'border-violet-200/60 from-violet-50 via-violet-50/90 to-violet-50/80 dark:border-violet-800/50 dark:from-violet-950/40 dark:via-violet-950/30 dark:to-violet-950/20',
      icon: 'bg-violet-100 dark:bg-violet-900/50',
      iconColor: 'text-violet-600 dark:text-violet-400',
      button: 'bg-violet-600 hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-600',
      nav: 'text-violet-600 hover:bg-violet-100 dark:text-violet-400 dark:hover:bg-violet-900/50',
      navDisabled: 'text-violet-300 dark:text-violet-700',
      dismiss:
        'text-violet-600 hover:bg-violet-200/50 focus:ring-violet-500 dark:text-violet-400 dark:hover:bg-violet-900/30'
    },
    WARNING: {
      container:
        'border-amber-200/60 from-amber-50 via-amber-50/90 to-amber-50/80 dark:border-amber-800/50 dark:from-amber-950/40 dark:via-amber-950/30 dark:to-amber-950/20',
      icon: 'bg-amber-100 dark:bg-amber-900/50',
      iconColor: 'text-amber-600 dark:text-amber-400',
      button: 'bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600',
      nav: 'text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/50',
      navDisabled: 'text-amber-300 dark:text-amber-700',
      dismiss:
        'text-amber-600 hover:bg-amber-200/50 focus:ring-amber-500 dark:text-amber-400 dark:hover:bg-amber-900/30'
    },
    ERROR: {
      container:
        'border-red-200/60 from-red-50 via-red-50/90 to-red-50/80 dark:border-red-800/50 dark:from-red-950/40 dark:via-red-950/30 dark:to-red-950/20',
      icon: 'bg-red-100 dark:bg-red-900/50',
      iconColor: 'text-red-600 dark:text-red-400',
      button: 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
      nav: 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/50',
      navDisabled: 'text-red-300 dark:text-red-700',
      dismiss:
        'text-red-600 hover:bg-red-200/50 focus:ring-red-500 dark:text-red-400 dark:hover:bg-red-900/30'
    },
    SUCCESS: {
      container:
        'border-emerald-200/60 from-emerald-50 via-emerald-50/90 to-emerald-50/80 dark:border-emerald-800/50 dark:from-emerald-950/40 dark:via-emerald-950/30 dark:to-emerald-950/20',
      icon: 'bg-emerald-100 dark:bg-emerald-900/50',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      button: 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600',
      nav: 'text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/50',
      navDisabled: 'text-emerald-300 dark:text-emerald-700',
      dismiss:
        'text-emerald-600 hover:bg-emerald-200/50 focus:ring-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-900/30'
    }
  }

  const getStyles = (type: string) =>
    styleConfig[type as keyof typeof styleConfig] || styleConfig.INFO

  const fetchAnnouncements = async () => {
    try {
      const response = await AnnouncementsService.announcementsActiveList()
      announcements.value = response
    } catch {
      announcements.value = []
    }
  }

  const hideAnnouncement = async (uuid: string) => {
    try {
      await AnnouncementsService.announcementsHideCreate({ uuid })
      announcements.value = announcements.value.filter((a) => a.uuid !== uuid)
    } catch {
      // Silently fail - user can retry
    }
  }

  const dismissAnnouncement = async (uuid: string) => {
    try {
      await AnnouncementsService.announcementsDismissCreate({ uuid })
      announcements.value = announcements.value.filter((a) => a.uuid !== uuid)
    } catch {
      // Silently fail - user can retry
    }
  }

  const shouldShowXIcon = (announcement: Announcement) => {
    return announcement.dismissal_state === DismissalStateEnum.SEEN
  }

  const goToPrev = () => {
    if (canGoPrev.value) currentIndex.value--
  }

  const goToNext = () => {
    if (canGoNext.value) currentIndex.value++
  }

  watch(visibleAnnouncements, (newVal) => {
    if (currentIndex.value >= newVal.length) {
      currentIndex.value = Math.max(0, newVal.length - 1)
    }
  })

  onMounted(() => {
    fetchAnnouncements()
  })
</script>

<template>
  <Transition name="announcement">
    <div
      v-if="currentAnnouncement"
      :class="[
        'relative overflow-hidden border-b bg-gradient-to-r backdrop-blur-sm transition-all duration-300',
        getStyles(currentAnnouncement.message_type).container
      ]"
    >
      <div
        class="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0
          dark:from-white/0 dark:via-white/5 dark:to-white/0"
      ></div>

      <div class="relative mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        <div class="flex items-center justify-center gap-3">
          <button
            v-if="hasMultipleAnnouncements"
            type="button"
            :disabled="!canGoPrev"
            :class="[
              'flex-shrink-0 rounded-md p-1 transition-colors',
              canGoPrev
                ? getStyles(currentAnnouncement.message_type).nav
                : 'cursor-not-allowed ' + getStyles(currentAnnouncement.message_type).navDisabled
            ]"
            @click="goToPrev"
          >
            <span class="sr-only">{{ t('previous') }}</span>
            <ChevronLeft class="h-5 w-5" />
          </button>

          <div
            :class="[
              'flex-shrink-0 rounded-full p-2 shadow-sm ring-1 ring-black/5 dark:ring-white/10',
              getStyles(currentAnnouncement.message_type).icon
            ]"
          >
            <MegaphoneIcon
              :class="['h-5 w-5', getStyles(currentAnnouncement.message_type).iconColor]"
            />
          </div>

          <div
            class="min-w-0 flex-1 text-center text-sm font-medium leading-relaxed text-gray-900 dark:text-gray-100"
            v-html="sanitizeHtml(currentAnnouncement.message)"
          ></div>

          <a
            v-if="safeLinkUrl"
            :href="safeLinkUrl"
            target="_blank"
            rel="noopener noreferrer"
            :class="[
              `inline-flex flex-shrink-0 items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-white
              transition-colors`,
              getStyles(currentAnnouncement.message_type).button
            ]"
          >
            {{ currentAnnouncement.link_text || t('learnMore') }}
            <ChevronRight class="h-4 w-4" />
          </a>

          <button
            v-if="hasMultipleAnnouncements"
            type="button"
            :disabled="!canGoNext"
            :class="[
              'flex-shrink-0 rounded-md p-1 transition-colors',
              canGoNext
                ? getStyles(currentAnnouncement.message_type).nav
                : 'cursor-not-allowed ' + getStyles(currentAnnouncement.message_type).navDisabled
            ]"
            @click="goToNext"
          >
            <span class="sr-only">{{ t('next') }}</span>
            <ChevronRight class="h-5 w-5" />
          </button>

          <template v-if="currentAnnouncement.dismissible">
            <button
              v-if="shouldShowXIcon(currentAnnouncement)"
              type="button"
              :class="[
                `ml-2 inline-flex flex-shrink-0 items-center justify-center rounded-lg p-1.5 transition-all
                duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-1
                focus:ring-offset-transparent active:scale-95`,
                getStyles(currentAnnouncement.message_type).dismiss
              ]"
              @click="dismissAnnouncement(currentAnnouncement.uuid)"
            >
              <span class="sr-only">{{ t('dismiss') }}</span>
              <X class="h-4 w-4" />
            </button>

            <button
              v-else
              type="button"
              class="ml-2 flex-shrink-0 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900
                dark:text-gray-400 dark:hover:text-gray-200"
              @click="hideAnnouncement(currentAnnouncement.uuid)"
            >
              {{ t('hideForNow') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
  .announcement-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .announcement-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
  }

  .announcement-enter-from {
    opacity: 0;
    transform: translateY(-100%);
    max-height: 0;
  }

  .announcement-leave-to {
    opacity: 0;
    transform: translateY(-100%);
    max-height: 0;
  }

  .announcement-enter-to,
  .announcement-leave-from {
    opacity: 1;
    transform: translateY(0);
    max-height: 200px;
  }
</style>
